import { fetchAuthSession } from "aws-amplify/auth";

import {
  buildAuditContextHeaderValue,
  getAuditContext,
} from "./audit/auditContextStore";

/**
 * Shared builder for the Amplify GraphQL client's request headers.
 *
 * NOTE (Amplify v6): `generateClient()` does NOT read this legacy
 * `API.graphql_headers` config, so this builder no longer sends the audit
 * header at runtime. The `X-Audit-Context` header is now supplied via the
 * shared `generateAuditedClient()` factory (`audit/auditedClient.ts`). This
 * function and the per-env `graphql_headers` wiring below are retained as
 * harmless dead config.
 *
 * This is the single injection point for the audit context header. It preserves
 * the existing Authorization behavior exactly — the Cognito access token taken
 * from the current authenticated session — and additionally attaches the audit
 * context to every operation issued through the shared client, with no
 * per-operation call-site change (Req 1.2):
 *   - `AuthUser`: the acting user's alias from the audit context store.
 *   - `X-Audit-Context`: the base64url-encoded audit context header value.
 *
 * Every environment config variant below (alpha, gamma, prod, alpha-2/3/4)
 * references this one builder for `graphql_headers`, so the header logic cannot
 * drift across environments.
 */
export const buildGraphqlHeaders = async () => {
  const { tokens } = await fetchAuthSession();
  return {
    Authorization: tokens?.accessToken,
    AuthUser: getAuditContext().alias,
    "X-Audit-Context": buildAuditContextHeaderValue(),
  };
};

export const getAlphaConfig = (callbackURL: string) => {
  return {
    Auth: {
      Cognito: {
        userPoolClientId: "35t6ffus0f8779aidpqvcj3slj",
        userPoolId: "us-east-1_qzm6gVTsT",
        loginWith: {
          // Optional
          oauth: {
            domain: "finn-alpha.auth.us-east-1.amazoncognito.com",
            scope: ["openid"],
            redirectSignIn: callbackURL,
            redirectSignOut: callbackURL,
            responseType: "code",
          },
          username: "true",
          email: "false", // Optional
          phone: "false", // Optional
        },
      },
    },
    oauth: {
      domain: "finn-alpha.auth.us-east-1.amazoncognito.com",
      scope: ["openid"],
      redirectSignIn: callbackURL,
      redirectSignOut: callbackURL,
      responseType: "code",
    },

    // Cognito options
    aws_project_region: "us-east-1",
    aws_cognito_identity_pool_id:
      "us-east-1:79093c63-0bdb-4e21-a20e-739b065f7089",
    aws_cognito_region: "us-east-1",
    aws_user_pools_id: "us-east-1_qzm6gVTsT",
    aws_user_pools_web_client_id: "35t6ffus0f8779aidpqvcj3slj",
    // AppSync
    aws_appsync_graphqlEndpoint:
      "https://3xvtg6hd25clvplgknf4oabkdi.appsync-api.us-east-1.amazonaws.com/graphql",
    aws_appsync_region: "us-east-1",
    aws_appsync_authenticationType: "AMAZON_COGNITO_USER_POOLS",
    API: {
      graphql_endpoint:
        "https://3xvtg6hd25clvplgknf4oabkdi.appsync-api.us-east-1.amazonaws.com/graphql",
      graphql_headers: buildGraphqlHeaders,
    },
  };
};
export const getGammaConfig = (callbackURL: string) => {
  return {
    Auth: {
      Cognito: {
        userPoolClientId: "32vqkpkihicso4gpnc62hfsqf3",
        userPoolId: "us-east-1_sfxbMXDYf",
        loginWith: {
          // Optional
          oauth: {
            domain: "finn-gamma.auth.us-east-1.amazoncognito.com",
            scope: ["openid"],
            redirectSignIn: callbackURL,
            redirectSignOut: callbackURL,
            responseType: "code",
          },
          username: "true",
          email: "false", // Optional
          phone: "false", // Optional
        },
      },
    },
    oauth: {
      domain: "finn-gamma.auth.us-east-1.amazoncognito.com",
      scope: ["openid"],
      redirectSignIn: callbackURL,
      redirectSignOut: callbackURL,
      responseType: "code",
    },
    // Cognito options
    aws_project_region: "us-east-1",
    aws_cognito_identity_pool_id:
      "us-east-1:dbf31438-5bd8-42f2-8d23-18a8307da84f",
    aws_cognito_region: "us-east-1",
    aws_user_pools_id: "us-east-1_sfxbMXDYf",
    aws_user_pools_web_client_id: "32vqkpkihicso4gpnc62hfsqf3",
    // AppSync
    aws_appsync_graphqlEndpoint:
      "https://nebmelp2nfakho4k3a6ydexeu4.appsync-api.us-east-1.amazonaws.com/graphql",
    aws_appsync_region: "us-east-1",
    aws_appsync_authenticationType: "AMAZON_COGNITO_USER_POOLS",
    API: {
      graphql_endpoint:
        "https://nebmelp2nfakho4k3a6ydexeu4.appsync-api.us-east-1.amazonaws.com/graphql",
      graphql_headers: buildGraphqlHeaders,
    },
  };
};
export const getProdConfig = (callbackURL: string) => {
  return {
    Auth: {
      Cognito: {
        userPoolClientId: "1jc6b0tlmqic0bhtu34p122qgk",
        userPoolId: "us-east-1_pWKGHHMDU",
        loginWith: {
          // Optional
          oauth: {
            domain: "finn-prod.auth.us-east-1.amazoncognito.com",
            scope: ["openid"],
            redirectSignIn: callbackURL,
            redirectSignOut: callbackURL,
            responseType: "code",
          },
          username: "true",
          email: "false", // Optional
          phone: "false", // Optional
        },
      },
    },
    oauth: {
      domain: "finn-prod.auth.us-east-1.amazoncognito.com",
      scope: ["openid"],
      redirectSignIn: callbackURL,
      redirectSignOut: callbackURL,
      responseType: "code",
    },
    // Cognito options
    aws_project_region: "us-east-1",
    aws_cognito_identity_pool_id:
      "us-east-1:97bd0593-3c7e-4176-801d-06fc31e4ab5b",
    aws_cognito_region: "us-east-1",
    aws_user_pools_id: "us-east-1_pWKGHHMDU",
    aws_user_pools_web_client_id: "1jc6b0tlmqic0bhtu34p122qgk",
    // AppSync
    aws_appsync_graphqlEndpoint:
      "https://6mfnfj77sbaqdiergb3nevxwzu.appsync-api.us-east-1.amazonaws.com/graphql",
    aws_appsync_region: "us-east-1",
    aws_appsync_authenticationType: "AMAZON_COGNITO_USER_POOLS",
    API: {
      graphql_endpoint:
        "https://6mfnfj77sbaqdiergb3nevxwzu.appsync-api.us-east-1.amazonaws.com/graphql",
      graphql_headers: buildGraphqlHeaders,
    },
  };
};

export const getAlpha2Config = (callbackURL: string) => {
  return {
    Auth: {
      Cognito: {
        userPoolClientId: "74fhn2a1jeelak5fcksjhrd6fk",
        userPoolId: "us-east-1_i3M2dkMur",
        loginWith: {
          oauth: {
            domain: "finn-alpha-2.auth.us-east-1.amazoncognito.com",
            scope: ["openid"],
            redirectSignIn: callbackURL,
            redirectSignOut: callbackURL,
            responseType: "code",
          },
          username: "true",
          email: "false",
          phone: "false",
        },
      },
    },
    oauth: {
      domain: "finn-alpha-2.auth.us-east-1.amazoncognito.com",
      scope: ["openid"],
      redirectSignIn: callbackURL,
      redirectSignOut: callbackURL,
      responseType: "code",
    },
    aws_project_region: "us-east-1",
    aws_cognito_identity_pool_id:
      "us-east-1:00a06d50-6f53-4aec-a2e2-456644057379",
    aws_cognito_region: "us-east-1",
    aws_user_pools_id: "us-east-1_i3M2dkMur",
    aws_user_pools_web_client_id: "74fhn2a1jeelak5fcksjhrd6fk",
    aws_appsync_graphqlEndpoint:
      "https://7kdtn2ie25alpemxg4fhafxuju.appsync-api.us-east-1.amazonaws.com/graphql",
    aws_appsync_region: "us-east-1",
    aws_appsync_authenticationType: "AMAZON_COGNITO_USER_POOLS",
    API: {
      graphql_endpoint:
        "https://7kdtn2ie25alpemxg4fhafxuju.appsync-api.us-east-1.amazonaws.com/graphql",
      graphql_headers: buildGraphqlHeaders,
    },
  };
};
export const getAlpha3Config = (callbackURL: string) => {
  return {
    Auth: {
      Cognito: {
        userPoolClientId: "5b7gj7tc78s5jbc2or0r0qulnp",
        userPoolId: "us-east-1_bm0b4G1tK",
        loginWith: {
          oauth: {
            domain: "finn-alpha-3.auth.us-east-1.amazoncognito.com",
            scope: ["openid"],
            redirectSignIn: callbackURL,
            redirectSignOut: callbackURL,
            responseType: "code",
          },
          username: "true",
          email: "false",
          phone: "false",
        },
      },
    },
    oauth: {
      domain: "finn-alpha-3.auth.us-east-1.amazoncognito.com",
      scope: ["openid"],
      redirectSignIn: callbackURL,
      redirectSignOut: callbackURL,
      responseType: "code",
    },
    aws_project_region: "us-east-1",
    aws_cognito_identity_pool_id:
      "us-east-1:1935e180-242d-4d31-a9a0-6e99f005f2cb",
    aws_cognito_region: "us-east-1",
    aws_user_pools_id: "us-east-1_bm0b4G1tK",
    aws_user_pools_web_client_id: "5b7gj7tc78s5jbc2or0r0qulnp",
    aws_appsync_graphqlEndpoint:
      "https://7hbg3urvbjfzjnc3j4edubas5u.appsync-api.us-east-1.amazonaws.com/graphql",
    aws_appsync_region: "us-east-1",
    aws_appsync_authenticationType: "AMAZON_COGNITO_USER_POOLS",
    API: {
      graphql_endpoint:
        "https://7hbg3urvbjfzjnc3j4edubas5u.appsync-api.us-east-1.amazonaws.com/graphql",
      graphql_headers: buildGraphqlHeaders,
    },
  };
};

export const getAlpha4Config = (callbackURL: string) => {
  return {
    Auth: {
      Cognito: {
        userPoolClientId: "5vir62t8h3qdagkr3s6omiumri",
        userPoolId: "us-east-1_XCtKST4TU",
        loginWith: {
          oauth: {
            domain: "finn-alpha-4.auth.us-east-1.amazoncognito.com",
            scope: ["openid"],
            redirectSignIn: callbackURL,
            redirectSignOut: callbackURL,
            responseType: "code",
          },
          username: "true",
          email: "false",
          phone: "false",
        },
      },
    },
    oauth: {
      domain: "finn-alpha-4.auth.us-east-1.amazoncognito.com",
      scope: ["openid"],
      redirectSignIn: callbackURL,
      redirectSignOut: callbackURL,
      responseType: "code",
    },
    aws_project_region: "us-east-1",
    aws_cognito_identity_pool_id:
      "us-east-1:08f3af90-e7ce-4732-9727-f84d45652775",
    aws_cognito_region: "us-east-1",
    aws_user_pools_id: "us-east-1_XCtKST4TU",
    aws_user_pools_web_client_id: "5vir62t8h3qdagkr3s6omiumri",
    aws_appsync_graphqlEndpoint:
      "https://fjcpcp7henhjjexvl63y7eioqy.appsync-api.us-east-1.amazonaws.com/graphql",
    aws_appsync_region: "us-east-1",
    aws_appsync_authenticationType: "AMAZON_COGNITO_USER_POOLS",
    API: {
      graphql_endpoint:
        "https://fjcpcp7henhjjexvl63y7eioqy.appsync-api.us-east-1.amazonaws.com/graphql",
      graphql_headers: buildGraphqlHeaders,
    },
  };
};

export const getAmplifyConfig = (url: string) => {
  // Local dev: map a dev-server port to a specific alpha cell so you can run
  // multiple envs side by side (each port must be registered as an allowed
  // callback URL on that cell's Cognito app client):
  //   3001 -> alpha cell 1   3002 -> alpha cell 2
  //   3003 -> alpha cell 3   3004 -> alpha cell 4
  // localhost:3000 (and any other local port) falls through to alpha cell 1.
  if (url.includes("localhost") || url.includes("127.0.0.1")) {
    if (url.includes(":3002")) {
      return getAlpha2Config(url);
    }
    if (url.includes(":3003")) {
      return getAlpha3Config(url);
    }
    if (url.includes(":3004")) {
      return getAlpha4Config(url);
    }
    // :3001, :3000, or unspecified -> alpha cell 1
    return getAlphaConfig(url);
  }

  if (url.includes("prod")) {
    return getProdConfig(url);
  }
  if (url.includes("gamma")) {
    return getGammaConfig(url);
  }
  // Alpha cell 2 - update Cognito/AppSync values after first deployment
  if (url.includes("alpha-2")) {
    return getAlpha2Config(url);
  }
  // Alpha cell 3 - update Cognito/AppSync values after first deployment
  if (url.includes("alpha-3")) {
    return getAlpha3Config(url);
  }
  // Alpha cell 4 - update Cognito/AppSync values after first deployment
  if (url.includes("alpha-4")) {
    return getAlpha4Config(url);
  }
  return getAlphaConfig(url);
};
